// 퀘스트 창(대표님 2026-10-24: "퀘창을 따로 두던가"): 마을에서만 여는 별도 창. 왼쪽 목록(진행 중 / 완료) + 오른쪽 상세(설명·보상·큰 버튼).
// 진행 중: 다음 목표 · 북쪽 지역 길 둘 · 마을 워프 ①②③ · 일일 의뢰. 완료: 지역별 스테이지 기록. 예전 마을 카드가 하던 일(자동 이동·워프)을 그대로 옮겼다.
// 열기: 가방 줄의 「퀘스트」 단추 · 추적기 글자 · G 키(hotkeys.ts) → 게임 명령 openQuest(영웅 입력은 게임이 막는다). 던전(전투 중)에서는 열리지 않는다.
// 닫기: Esc(게임이 닫는다) · G · 닫기 단추 · 바깥 누르기. 큰 버튼은 창을 닫은 다음 실행한다(창이 열린 동안은 자동 이동·가방이 막혀 있다).
// 글자는 실제 화면 12px 이상, 누르는 칸은 터치 기기에서 44px 이상. 틀 그림은 기존 UI 아트(frame-quest)를 쓴다(새 그림 없음).
import { autoActive } from '../shared/autoRoute';
import { VILLAGES } from '../shared/levels';
import { Scene, type GameCommand, type GameView } from '../shared/types';
import { viewH, viewW } from '../shared/viewport';
import { AUTO_QUEST_EVENT } from './autoQuest';
import { C, FONT, el } from './dom';
import { DAILY_OPEN_EVENT, QUEST_TAB_EVENT } from './uiEvents';
import { buildAdventureRows, buildQuestWindow, claimBlocked, claimLockUntil, type QuestRow, type QuestRun, type QuestTab, type QuestWindowModel } from './questWindowModel';
import { TIER_STYLE } from './regionCard';
import { uiSizeFactor } from './uiSize';
import { applyFrame, iconOver, regionEmblem, townCrest, uiIcon } from './uiArt';
import type { QuestGoalView } from './villageQuestModel';


const SHADOW = 'text-shadow:0 1px 0 #000,0 2px 4px rgba(0,0,0,.75);';
const HIT_STYLE_ID = 'dh-qw-hit';
// 보이는 크기는 그대로 두고 누르는 칸만 위아래로 넓힌다(탭 34→44, 닫기 36→44). 이웃 버튼과 가로로 겹치지 않는다.
const HIT_CSS = '.dh-qw-hit{position:relative}.dh-qw-hit::after{content:"";position:absolute;left:0;right:0;top:calc(50% - 22px);height:44px}' +
  '.dh-qw-hit-r::after{left:calc(50% - 22px);right:auto;width:44px}';
function ensureHitStyle(): void {
  if (document.getElementById(HIT_STYLE_ID)) return;
  const st = document.createElement('style');
  st.id = HIT_STYLE_ID;
  st.textContent = HIT_CSS;
  document.head.appendChild(st);
}
const LOCK_STYLE = { border: '#5a6078', badgeBg: '#1e2234', badgeText: '#9aa2bc' };
const GOLD_STYLE = { border: '#d9a441', badgeBg: '#4a3a10', badgeText: '#ffe7a8' };
const DONE_STYLE = { border: '#6fcf8a', badgeBg: '#12402a', badgeText: '#b8ffd0' };
const STYLE_OF = (t: QuestRow['tone']): { border: string; badgeBg: string; badgeText: string } =>
  t === 'lock' ? LOCK_STYLE : t === 'gold' ? GOLD_STYLE : t === 'done' ? DONE_STYLE : TIER_STYLE[t];
const GLYPH: Record<QuestRow['kind'], string> = { goal: '★', milestone: '◆', door: '⛩', warp: '⇄', daily: '☀', record: '✓', bounty: '⚔', hunt: '✦', codex: '📖', trial: '⚔', board: '🏆' };

/**
 * 창 크기 계산(순수): W×H 화면에서 창의 CSS 폭·높이와 PC 확대 배율. 좁은 창(narrow)은 목록과 상세를 위아래로 쌓는다.
 * userScale = 설정의 UI 크기(0.9 / 1 / 1.15). HUD와 같은 규칙으로 가로 1000px 이상에서만 곱한다(폰·좁은 화면은 그대로)
 */
export function windowSize(W: number, H: number, userScale = 1): { zoom: number; width: number; height: number; narrow: boolean; short: boolean } {
  const zoom = W >= 1000 ? Math.max(1, Math.min(1.5, Math.min(W / 1280, H / 720))) * userScale : 1;
  const width = Math.floor(Math.min(880, (W - 20) / zoom));
  // 세로 화면은 목록·상세를 쌓으므로 더 높게 쓴다(390x844에서 큰 버튼이 잘리던 것)
  const height = Math.floor(Math.min(W < H ? 720 : 470, (H - 20) / zoom));
  // 낮은 가로 화면(568x320 등): 머리줄에 탭을 함께 넣고 여백을 줄여 상세 칸 높이를 확보한다
  return { zoom, width, height, narrow: W < H || width < 520, short: H < 420 };
}

export class QuestWindow {
  private readonly backdrop: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private readonly crestSlot: HTMLDivElement;
  private readonly place: HTMLDivElement;
  private readonly tabsBar: HTMLDivElement;
  private readonly head: HTMLDivElement;
  private readonly closeBtn: HTMLButtonElement;
  private readonly tabBtns: Record<QuestTab, HTMLButtonElement>;
  private readonly list: HTMLDivElement;
  private readonly detail: HTMLDivElement;
  private readonly bodyEl: HTMLDivElement;
  private shown = false;
  private tab: QuestTab = 'active';
  /** 다음에 열 때 처음 보일 탭(QUEST_TAB_EVENT). 열리면 비운다 */
  private openTab: QuestTab | null = null;
  private sel = 0;
  private model: QuestWindowModel = { active: [], done: [], adventure: [], trial: [] };
  private kKey = '';
  private kSize = '';
  private pending: QuestRun | null = null;
  private goal: QuestGoalView | null = null;
  private kActive = '-';
  private claimLock = 0;

  constructor(parent: HTMLElement, private readonly view: GameView, private readonly send: (c: GameCommand) => void, private readonly touch: boolean) {
    ensureHitStyle();
    this.backdrop = el('div', 'position:absolute;inset:0;z-index:40;display:none;align-items:center;justify-content:center;pointer-events:auto;touch-action:none;background:rgba(6,8,18,.62);', parent);
    this.backdrop.dataset.testid = 'quest-window';
    window.addEventListener(QUEST_TAB_EVENT, (e) => {
      const t = (e as CustomEvent<QuestTab>).detail;
      if (t !== 'active' && t !== 'done' && t !== 'adventure' && t !== 'trial') return;
      if (this.shown) { this.tab = t; this.sel = 0; this.kKey = ''; } else this.openTab = t;
    });
    this.backdrop.addEventListener('pointerdown', (e) => { if (e.target === this.backdrop) this.send({ type: 'closeMenu' }); });
    this.backdrop.addEventListener('wheel', (e) => e.stopPropagation(), { passive: true });
    this.backdrop.addEventListener('contextmenu', (e) => e.preventDefault());
    this.panel = el('div', 'position:relative;box-sizing:border-box;display:flex;flex-direction:column;overflow:hidden;color:#e6ecff;font-family:' + FONT + ';' +
      'border:2px solid #d9a441;border-radius:14px;background:linear-gradient(180deg,rgba(22,30,58,.98),rgba(10,15,32,.98));box-shadow:0 12px 40px rgba(0,0,0,.65);', this.backdrop);
    this.panel.tabIndex = -1;
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-modal', 'true');
    this.panel.setAttribute('aria-label', '퀘스트');
    this.panel.dataset.testid = 'quest-panel';
    // 금테 틀(기존 UI 아트 frame-quest): 받았을 때만 입히고, 받지 못하면 위 CSS 테두리가 그대로 남는다
    applyFrame(this.panel, 'frame-quest', 9, 16);
    // 포커스된 버튼에서 Enter·Space가 게임 입력(상호작용·각성)으로 새지 않게 한다
    this.panel.addEventListener('keydown', (e) => {
      if (e.code === 'Enter' || e.code === 'NumpadEnter' || e.code === 'Space') e.stopPropagation();
      this.onKey(e);
    });

    const head = this.head = el('div', 'flex:0 0 auto;display:flex;align-items:center;gap:10px;padding:10px 14px 6px 16px;', this.panel);
    this.crestSlot = el('div', 'flex:0 0 auto;width:34px;height:34px;display:grid;place-items:center;filter:drop-shadow(0 0 6px rgba(242,193,78,.45));', head);
    const tt = el('div', 'flex:1 1 auto;min-width:0;', head);
    el('div', 'font:900 20px ' + FONT + ';letter-spacing:1px;color:#ffe7a8;' + SHADOW, tt, '퀘스트');
    this.place = el('div', 'font:700 13px ' + FONT + ';color:#9fb0d8;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;', tt, '');
    if (!touch) el('span', 'flex:0 0 auto;padding:2px 8px;border-radius:6px;border:1.5px solid ' + C.gold + ';background:linear-gradient(180deg,#26335a,#10162a);color:' + C.gold + ';font:800 12px ' + FONT + ';', head, 'G · Esc');
    const close = this.closeBtn = el('button', 'flex:0 0 auto;cursor:pointer;width:' + (touch ? 44 : 36) + 'px;height:' + (touch ? 44 : 36) + 'px;border-radius:50%;border:1.5px solid #e2b24a;background:linear-gradient(180deg,#2c3a68,#121a36);color:#ffe7a8;font:900 16px ' + FONT + ';', head, '✕') as HTMLButtonElement;
    close.type = 'button';
    close.dataset.testid = 'quest-close';
    close.className = 'dh-qw-hit dh-qw-hit-r';
    close.setAttribute('aria-label', '퀘스트 창 닫기');
    close.setAttribute('data-tip', '닫기 (Esc)');
    close.addEventListener('click', () => this.send({ type: 'closeMenu' }));

    this.tabsBar = el('div', 'flex:0 0 auto;display:flex;gap:8px;padding:2px 16px 8px;', this.panel);
    this.tabsBar.setAttribute('role', 'tablist');
    this.tabBtns = { active: this.makeTab('active', '진행 중'), done: this.makeTab('done', '완료'), adventure: this.makeTab('adventure', '모험'), trial: this.makeTab('trial', '시련') };
    this.bodyEl = el('div', 'flex:1 1 auto;min-height:0;display:flex;gap:10px;padding:0 14px 14px;', this.panel);
    this.list = el('div', 'min-height:0;overflow:auto;display:flex;flex-direction:column;gap:7px;padding:2px 4px 2px 2px;', this.bodyEl);
    this.list.dataset.testid = 'quest-list';
    this.list.setAttribute('role', 'listbox');
    this.detail = el('div', 'min-height:0;overflow:auto;box-sizing:border-box;padding:12px 14px;border-radius:10px;border:1.5px solid rgba(242,193,78,.4);background:rgba(6,9,22,.55);', this.bodyEl);
    this.detail.dataset.testid = 'quest-detail';
    this.detail.setAttribute('aria-live', 'polite');
  }

  private makeTab(t: QuestTab, label: string): HTMLButtonElement {
    const b = el('button', 'cursor:pointer;min-height:' + (this.touch ? 44 : 34) + 'px;padding:0 18px;border-radius:999px;font:800 15px ' + FONT + ';', this.tabsBar, label) as HTMLButtonElement;
    b.type = 'button';
    b.className = 'dh-qw-hit';
    b.setAttribute('role', 'tab');
    b.dataset.testid = 'quest-tab-' + t;
    b.addEventListener('click', () => { this.tab = t; this.sel = 0; this.kKey = ''; });
    return b;
  }

  /** 열려 있는가(시험·QA용) */
  get isShown(): boolean { return this.shown; }

  /** 시련 탭 글자 옆 숫자 = 이룬 시련 수 */
  private trialDone(): number { return this.view.trials ? this.view.trials.done.length : 0; }

  private rows(): readonly QuestRow[] { return this.model[this.tab]; }

  private onKey(e: KeyboardEvent): void {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const rows = this.rows();
    if (e.code === 'ArrowDown' || e.code === 'ArrowUp') {
      if (!rows.length) return;
      this.sel = Math.max(0, Math.min(rows.length - 1, this.sel + (e.code === 'ArrowDown' ? 1 : -1)));
      this.kKey = '';
      e.preventDefault();
    } else if (e.code === 'ArrowLeft' || e.code === 'ArrowRight') {
      const order: readonly QuestTab[] = ['active', 'done', 'adventure', 'trial'];
      this.tab = order[Math.max(0, Math.min(3, order.indexOf(this.tab) + (e.code === 'ArrowLeft' ? -1 : 1)))];
      this.sel = 0; this.kKey = '';
      e.preventDefault();
    }
  }

  /** 큰 버튼: 창을 닫고, 닫힌 것이 확인되면(update) 실행한다 */
  private runAction(a: QuestRun): void {
    // 도감 보상 받기: 창을 닫지 않고 바로 보낸다(결과는 같은 줄이 「받음」으로 바뀐다)
    if (a.kind === 'claim') {
      // 받은 직후 같은 자리 버튼이 다음 단계로 바뀌므로, 잠시 동안 보상 받기 클릭을 무시한다(연타로 다음 단계까지 받는 것 방지)
      const now = performance.now();
      if (claimBlocked(now, this.claimLock)) return;
      this.claimLock = claimLockUntil(now);
      this.send({ type: 'adventureClaim', step: a.step }); this.kKey = ''; return;
    }
    // 시련 상자 열기: 도감 보상과 같은 연타 잠금(열린 직후 같은 자리 버튼이 「받음」으로 바뀐다)
    if (a.kind === 'trialChest') {
      const now = performance.now();
      if (claimBlocked(now, this.claimLock)) return;
      this.claimLock = claimLockUntil(now);
      this.send({ type: 'claimTrialChest', board: a.board }); this.kKey = ''; return;
    }
    this.pending = a;
    this.send({ type: 'closeMenu' });
  }

  private perform(a: QuestRun): void {
    if (a.kind === 'claim' || a.kind === 'trialChest') return;
    if (a.kind === 'trial') { this.send({ type: 'enterTrial', trial: a.id }); return; }
    if (a.kind === 'daily') { window.dispatchEvent(new CustomEvent(DAILY_OPEN_EVENT)); return; }
    if (a.action.kind === 'bag') { this.send({ type: 'openInventory' }); return; }
    window.dispatchEvent(new CustomEvent(AUTO_QUEST_EVENT, { detail: a.action }));
  }

  update(_u: number, goal: QuestGoalView | null, place: string, doorOpen: ((id: string) => boolean) | null, town: number, highest: number): void {
    const v = this.view;
    const on = v.flow.scene === Scene.Village && v.village.questOpen === true && !v.flow.paused;
    if (this.pending && !v.village.questOpen) {
      const a = this.pending; this.pending = null;
      if (v.flow.scene === Scene.Village) this.perform(a);
    }
    if (!on) {
      this.openTab = null;
      if (this.shown) { this.shown = false; this.backdrop.style.display = 'none'; if (this.panel.contains(document.activeElement)) (document.activeElement as HTMLElement).blur(); }
      return;
    }
    if (!this.shown) {
      this.shown = true;
      this.tab = this.openTab ?? 'active'; this.openTab = null; this.sel = 0; this.kKey = ''; this.kSize = '';
      this.backdrop.style.display = 'flex';
    }
    this.goal = goal;
    const W = viewW(), H = viewH();
    const userScale = uiSizeFactor(W);
    const sz = windowSize(W, H, userScale);
    const sizeKey = W + 'x' + H + ':' + userScale.toFixed(2);
    if (sizeKey !== this.kSize) {
      this.kSize = sizeKey;
      this.panel.style.zoom = String(sz.zoom);
      this.panel.style.width = sz.width + 'px';
      this.panel.style.height = sz.height + 'px';
      this.bodyEl.style.flexDirection = sz.narrow ? 'column' : 'row';
      this.list.style.flex = sz.narrow ? '0 0 38%' : '0 0 40%';
      this.list.style.maxWidth = sz.narrow ? '' : '340px';
      // 낮은 화면: 탭을 머리줄 안(닫기 단추 앞)으로 옮기고 마을 이름 줄을 숨긴다
      if (sz.short && this.tabsBar.parentElement !== this.head) { this.head.insertBefore(this.tabsBar, this.closeBtn); this.tabsBar.style.padding = '0'; }
      else if (!sz.short && this.tabsBar.parentElement === this.head) { this.panel.insertBefore(this.tabsBar, this.bodyEl); this.tabsBar.style.padding = '2px 16px 8px'; }
      this.place.style.display = sz.short ? 'none' : '';
      this.head.style.padding = sz.short ? '8px 12px 6px 14px' : '10px 14px 6px 16px';
      this.detail.style.flex = '1 1 0';
      this.kKey = '';
    }
    const pr = v.progress, adv = v.adventure;
    const key = [place, town, highest, adv ? (adv.claimed ?? []).join(',') : '', pr ? pr.revision + ':' + pr.cleared : '', v.hard ? v.hard.revision : '', v.hard ? v.hard.cleared : '', v.growth.level, goal ? goal.name + goal.change + goal.detail : '-', adv ? adv.revision + ':' + adv.day : '-', v.avatar ? v.avatar.revision : '-', v.trials ? v.trials.rev : '-', this.tab, this.sel, autoActive.key].join('|');
    if (key === this.kKey) return;
    this.kKey = key;
    this.model = buildQuestWindow({
      goal, place, town, highestTown: highest, townNames: VILLAGES.map((x) => x.name), exits: v.map.exits, open: doorOpen,
      progress: pr, hard: v.hard, records: v.records, level: v.growth.level, clearedNormal: pr?.cleared, clearedHard: v.hard?.cleared, trials: v.trials,
    });
    this.model = { ...this.model, adventure: buildAdventureRows(adv, v.avatar?.owned ?? []) };
    this.render(place, town);
  }

  private render(place: string, town: number): void {
    const rows = this.rows();
    if (this.sel >= rows.length) this.sel = Math.max(0, rows.length - 1);
    this.place.textContent = place;
    this.crestSlot.textContent = '';
    const crest = townCrest(town);
    if (crest) uiIcon(crest, 32, this.crestSlot);
    for (const t of ['active', 'done', 'adventure', 'trial'] as const) {
      const b = this.tabBtns[t], on = this.tab === t, n = t === 'trial' ? this.trialDone() : this.model[t].length;
      b.textContent = (t === 'active' ? '진행 중' : t === 'done' ? '완료' : t === 'adventure' ? '모험' : '시련') + ' ' + n;
      b.setAttribute('aria-selected', on ? 'true' : 'false');
      b.style.border = '1.5px solid ' + (on ? '#f2c14e' : '#4a5580');
      b.style.background = on ? 'linear-gradient(180deg,#5a4614,#2a2008)' : 'linear-gradient(180deg,#26335a,#10162a)';
      b.style.color = on ? '#ffe7a8' : '#b9c4e6';
    }
    const focusId = (document.activeElement as HTMLElement | null)?.dataset?.rowid ?? '';
    this.list.textContent = '';
    if (!rows.length) {
      el('div', 'padding:14px;font:600 14px/1.5 ' + FONT + ';color:#9fb0d8;', this.list, this.tab === 'done' ? '아직 완료한 퀘스트가 없어요.\n첫 지역을 클리어하면 여기에 기록돼요.' : this.tab === 'adventure' ? '이 화면에서는 모험 목표판을 볼 수 없어요.' : this.tab === 'trial' ? '이 화면에서는 시련을 볼 수 없어요.' : '진행 중인 퀘스트가 없어요.').style.whiteSpace = 'pre-line';
    }
    rows.forEach((r, i) => this.drawRow(r, i, i === this.sel));
    this.drawDetail(rows[this.sel] ?? null);
    if (focusId) (this.list.querySelector('[data-rowid="' + focusId + '"]') as HTMLElement | null)?.focus({ preventScroll: true });
    else if (document.activeElement === document.body || !this.panel.contains(document.activeElement)) this.panel.focus({ preventScroll: true });
  }

  private drawRow(r: QuestRow, i: number, selected: boolean): void {
    const st = STYLE_OF(r.tone);
    const b = el('button', 'flex:0 0 auto;text-align:left;cursor:pointer;display:flex;align-items:center;gap:10px;box-sizing:border-box;min-height:' + (this.touch ? 54 : 50) + 'px;padding:7px 10px;border-radius:9px;' +
      'border:1.5px solid ' + (selected ? '#f2c14e' : st.border) + ';background:' + (selected ? 'linear-gradient(90deg,rgba(90,70,20,.9),rgba(14,20,40,.95))' : 'linear-gradient(90deg,' + st.badgeBg + ' 0%,rgba(12,18,34,.92) 100%)') + ';' +
      (selected ? 'box-shadow:0 0 0 2px rgba(242,193,78,.35);' : '') + 'color:#fff;font-family:' + FONT + ';', this.list) as HTMLButtonElement;
    b.type = 'button';
    b.dataset.rowid = r.id;
    b.dataset.testid = 'quest-row-' + r.id;
    b.setAttribute('role', 'option');
    b.setAttribute('aria-selected', selected ? 'true' : 'false');
    b.addEventListener('click', () => { this.sel = i; this.kKey = ''; });
    const ic = el('div', 'flex:0 0 auto;width:34px;height:34px;display:grid;place-items:center;box-sizing:border-box;border-radius:7px;border:1px solid ' + st.border + ';background:rgba(0,0,0,.25);font:900 17px ' + FONT + ';color:' + st.badgeText + ';', b);
    if (r.region) iconOver(ic, regionEmblem(r.region), 30, '<span>' + GLYPH[r.kind] + '</span>', r.tone === 'lock' ? 'filter:grayscale(1) brightness(.6);' : '');
    else ic.textContent = GLYPH[r.kind];
    const t = el('div', 'flex:1 1 auto;min-width:0;display:flex;flex-direction:column;gap:1px;line-height:1.25;', b);
    const top = el('div', 'display:flex;align-items:center;gap:7px;min-width:0;', t);
    // 시련 목록 줄은 좁은 폭(인앱 390)에서도 말줄임 없이 두 줄로 다 보인다(출시 게이트 RG-009)
    el('div', 'flex:0 1 auto;min-width:0;font:800 15px ' + FONT + ';white-space:normal;word-break:keep-all;overflow-wrap:anywhere;' + SHADOW, top, r.title);
    el('span', 'flex:0 0 auto;padding:0 8px;border-radius:999px;border:1.5px solid ' + st.border + ';background:' + st.badgeBg + ';color:' + st.badgeText + ';font:800 12px ' + FONT + ';line-height:18px;white-space:nowrap;', top, r.tag);
    el('div', 'font:600 13px ' + FONT + ';color:#c9d4f5;white-space:normal;word-break:keep-all;overflow-wrap:anywhere;', t, r.sub);
  }

  private drawDetail(r: QuestRow | null): void {
    const d = this.detail;
    d.textContent = '';
    if (!r) return;
    const st = STYLE_OF(r.tone);
    const head = el('div', 'display:flex;align-items:center;gap:8px;flex-wrap:wrap;', d);
    el('div', 'font:900 19px ' + FONT + ';color:#fff;' + SHADOW, head, r.title);
    el('span', 'padding:0 9px;border-radius:999px;border:1.5px solid ' + st.border + ';background:' + st.badgeBg + ';color:' + st.badgeText + ';font:800 12px ' + FONT + ';line-height:20px;', head, r.tag);
    for (const line of r.lines) el('div', 'margin-top:7px;font:600 14px/1.45 ' + FONT + ';color:#dfe6ff;word-break:keep-all;', d, line);
    if (r.rewards.length) {
      const box = el('div', 'margin-top:11px;padding:8px 11px;border-radius:9px;border:1.5px solid rgba(242,193,78,.5);background:rgba(60,46,10,.45);', d);
      el('div', 'font:900 13px ' + FONT + ';color:' + C.gold + ';letter-spacing:.5px;', box, '보상');
      for (const line of r.rewards) el('div', 'margin-top:3px;font:700 14px/1.4 ' + FONT + ';color:#fff;word-break:keep-all;', box, line);
    }
    if (r.chips) {
      const row = el('div', 'margin-top:12px;display:flex;flex-wrap:wrap;gap:8px;', d);
      for (const c of r.chips) {
        const b = el('button', 'cursor:' + (c.current ? 'default' : 'pointer') + ';display:flex;align-items:center;gap:5px;min-height:' + (this.touch ? 44 : 38) + 'px;padding:0 14px;border-radius:9px;font:900 15px ' + FONT + ';' +
          'border:1.5px solid ' + (c.current ? '#f2c14e' : '#6f7aa0') + ';' + (c.current ? 'background:linear-gradient(180deg,#5a4614,#2a2008);color:#ffe7a8;' : 'background:linear-gradient(180deg,#26335a,#10162a);color:#dfe6ff;'), row) as HTMLButtonElement;
        b.type = 'button';
        const crest = townCrest(c.town);
        if (crest) uiIcon(crest, 24, b);
        el('span', '', b, c.label + ' ' + c.name);
        b.dataset.testid = 'quest-warp-' + c.town;
        if (c.current) { b.setAttribute('aria-current', 'true'); b.disabled = true; b.title = '지금 있는 마을'; }
        else {
          b.setAttribute('aria-label', c.name + '(으)로 워프');
          // 닫은 뒤 이동: 같은 틱에 순서대로 처리된다(closeMenu → travel)
          b.addEventListener('click', () => { this.send({ type: 'closeMenu' }); this.send({ type: 'travel', town: c.town }); });
        }
      }
    }
    if (r.action) {
      const a = r.action;
      const on = a.run.kind === 'auto' && ((a.run.action.kind === 'door' && autoActive.key === 'door:' + a.run.action.region) || (a.run.action.kind === 'npc' && autoActive.key === 'npc:' + a.run.action.role) || (a.run.action.kind === 'gate' && autoActive.key === 'gate'));
      const b = el('button', 'margin-top:14px;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;min-height:' + (this.touch ? 48 : 44) + 'px;padding:0 22px;border-radius:999px;border:2px solid #e2b24a;' +
        'background:linear-gradient(180deg,#f7d57a,#c98c22);color:#2a1a04;font:900 16px ' + FONT + ';box-shadow:0 2px 0 rgba(0,0,0,.45),0 0 12px rgba(242,193,78,.45);', d, on ? '진행 중 · 다시 누르면 멈춤' : a.label) as HTMLButtonElement;
      b.type = 'button';
      b.dataset.testid = 'quest-action';
      b.addEventListener('click', () => this.runAction(a.run));
    } else if (r.blocked) {
      el('div', 'margin-top:12px;padding:8px 11px;border-radius:9px;border:1.5px solid ' + LOCK_STYLE.border + ';background:rgba(30,34,52,.7);font:700 14px/1.4 ' + FONT + ';color:#b9c1dc;', d, r.blocked).dataset.testid = 'quest-blocked';
    }
  }
}
